import type { TicketStatus } from '@/types/support-ticket';

export type IntakeRequestType = 'issue' | 'feature_request' | 'feedback';

export const INTAKE_REQUEST_TYPES: IntakeRequestType[] = [
  'issue',
  'feature_request',
  'feedback',
];

// Same 4 values as the retired FeedbackCategory (see the feedback feature's
// own type module, deleted in Plan 02), per D-04 — not module-derived, not a
// new list. Do NOT import that module from here.
export type IntakeProduct = 'box_os' | 'marketing' | 'smartlev' | 'training';

export const INTAKE_PRODUCTS: IntakeProduct[] = ['box_os', 'marketing', 'smartlev', 'training'];

export type IntakeSeverity = 'regular' | 'critical';

export const INTAKE_SEVERITIES: IntakeSeverity[] = ['regular', 'critical'];

// Copied verbatim from the prior screenshot-attachment zone's constants
// (max size MB / accept list), retired alongside the old widget in Plan 02.
export const INTAKE_ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,application/pdf';
// Matches the API's MAX_ATTACHMENT_BYTES (5MB) enforced by validateAttachments,
// so oversize files are caught client-side instead of failing at escalation.
export const INTAKE_ATTACHMENT_MAX_MB = 5;

/**
 * Contract handed to Phase 409's submission handler via IntakePanel's
 * `onSubmit` prop. Wired by Phase 409 via useIntakeWizard — this is the seam
 * Phase 408 left open (408-CONTEXT phase boundary).
 */
export interface IntakeFormData {
  requestType: IntakeRequestType;
  product: IntakeProduct;
  locationId: string;
  whatHappened: string;
  whosAffected?: string;
  severity: IntakeSeverity;
  attachment: File | null;
}

/** A candidate member/lead surfaced by the API's text-based resolution step. */
export interface IntakeCandidate {
  type: 'member' | 'lead';
  id: string;
  displayName: string;
  secondaryLabel: string | null;
}

/** AI-phrased suggested fix shown on the fix card (FIXMATCH-02). */
export interface IntakeFixSuggestion {
  sourceLine: string;
  steps: string[];
}

/** Ticket summary shown on the escalation confirmation screen (ESCFORM-04). */
export interface IntakeTicketConfirmation {
  id: string;
  ticketNumber: string;
  status: TicketStatus;
  severity: IntakeSeverity;
  requestType: IntakeRequestType;
  summary: string;
}

/** The admin's answer to the candidate-confirmation step (D-04/D-06). */
export interface IntakeCandidateChoice {
  candidateType: 'member' | 'lead' | 'none';
  candidateId?: string;
}

export type IntakeCheckResponse =
  | { step: 'candidate_confirm'; candidates: IntakeCandidate[] }
  | { step: 'fix_card'; fix: IntakeFixSuggestion; escalationToken: string }
  | { step: 'no_fix'; escalationToken: string };

export type IntakeEscalateResponse = {
  step: 'escalation_confirmation';
  ticket: IntakeTicketConfirmation;
};

/** One-way wizard step machine driving the inline IntakePanel flow (D-12/D-15). */
export type IntakeWizardStep =
  | 'form'
  | 'candidate_confirm'
  | 'diagnostic_loading'
  | 'fix_card'
  | 'resolved'
  | 'escalation_loading'
  | 'escalation_confirmation';
